/**
 * Treatment Recommender – by suggestion.
 * Full-width suggestion cards with client photo, feature breakdown, and View examples.
 */

import { useMemo, useState, useEffect } from "react";
import { useDashboard } from "../../context/DashboardContext";
import { Client, DiscussedItem } from "../../types";
import {
  fetchTableRecords,
  fetchPatientRecords,
  getAreaCroppedPhotoUrl,
  parsePatientRecordsToCards,
  type PatientSuggestionCard,
  PATIENT_RECORDS_PHOTO_FIELD,
  PATIENT_RECORDS_SUGGESTION_NAME_FIELD,
} from "../../services/api";
import {
  isHiddenAnalysisIssue,
  normalizeIssue,
  scoreTier,
  tierColor,
  scoreIssues,
} from "../../config/analysisOverviewConfig";
import {
  DEFAULT_RECOMMENDER_FILTER_STATE,
  filterSuggestionsByRegion,
  AREA_CROPPED_PHOTO_FIELDS,
  type TreatmentRecommenderFilterState,
} from "../../config/treatmentRecommenderConfig";
import { SAME_DAY_TREATMENTS } from "../../config/treatmentRecommenderConfig";
import {
  ALL_TREATMENT_INTERESTS,
  SUGGESTION_TO_AREA,
  SUGGESTION_TO_ISSUES,
} from "../modals/DiscussedTreatmentsModal/suggestionsMapping";
import {
  getTreatmentsForInterest,
  getQuantityContext,
  shouldShowProminentPlanQuantity,
  timelineOptionDisplayLabel,
} from "../modals/DiscussedTreatmentsModal/utils";
import {
  REGION_OPTIONS,
  TIMELINE_OPTIONS,
  getSkincareCarouselItems,
} from "../modals/DiscussedTreatmentsModal/constants";
import {
  GEMSTONE_BY_SKIN_TYPE,
  buildQuizSkincareRoutineSections,
} from "../../data/skinTypeQuiz";
import { showToast } from "../../utils/toast";
import { getClientFrontPhotoDisplayUrl } from "../../utils/photoLoading";
import { groupIssuesByConcern } from "../../config/issueToConcernMapping";
import type {
  TreatmentPlanAddDirectOptions,
  TreatmentPlanPrefill,
} from "../modals/DiscussedTreatmentsModal/TreatmentPhotos";
import { isWellnestWellnessProviderCode } from "../../data/wellnestOfferings";
import TreatmentRecommenderFilters from "./TreatmentRecommenderFilters";
import { PlanQuantityStepperInput } from "./planQuantityStepper";
import TreatmentPhotosModal from "../modals/TreatmentPhotosModal";
import "../modals/AnalysisOverviewModal.css";
import "./TreatmentRecommenderBySuggestion.css";

/** Safe fragment for DOM ids in optional-details sections (treatment names may include spaces). */
function planOptDomIdSuffix(treatmentName: string): string {
  const s = treatmentName
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9_-]/g, "");
  return s.length > 0 ? s : "treatment";
}

function getDetectedIssues(client: Client): Set<string> {
  const set = new Set<string>();
  const raw = client.allIssues;
  if (!raw) return set;
  const list = Array.isArray(raw)
    ? raw
    : String(raw)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
  list.forEach((issue) => {
    if (!isHiddenAnalysisIssue(issue)) set.add(normalizeIssue(issue));
  });
  return set;
}

/** Collapsible feature breakdown row – same pattern as by-treatment. */
function FeatureBreakdownRow({
  label,
  issues,
  detectedIssues,
}: {
  label: string;
  issues: string[];
  detectedIssues: Set<string>;
}) {
  const [expanded, setExpanded] = useState(false);
  const score = scoreIssues(issues, detectedIssues);
  const color = tierColor(scoreTier(score));
  const goodIssues = issues.filter(
    (i) => !detectedIssues.has(normalizeIssue(i)),
  );
  const badIssues = issues.filter((i) => detectedIssues.has(normalizeIssue(i)));

  if (issues.length === 0) return null;

  return (
    <div
      className={`ao-subscore-row ${expanded ? "ao-subscore-row--open" : ""}`}
    >
      <button
        type="button"
        className="ao-subscore-row__header"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="ao-subscore-row__name">{label}</span>
        <div className="ao-subscore-row__bar-wrap">
          <div className="ao-subscore-row__bar-track">
            <div
              className="ao-subscore-row__bar-fill"
              style={{ width: `${score}%`, background: color }}
            />
          </div>
          <span className="ao-subscore-row__score" style={{ color }}>
            {score}
          </span>
        </div>
        <span className="ao-subscore-row__chev" aria-hidden>
          {expanded ? "▲" : "▼"}
        </span>
      </button>
      {expanded && (
        <div className="ao-subscore-row__pills">
          {goodIssues.map((issue) => (
            <span key={issue} className="ao-pill ao-pill--good">
              <span className="ao-pill__icon">✓</span>
              {issue}
            </span>
          ))}
          {badIssues.map((issue) => (
            <span key={issue} className="ao-pill ao-pill--concern">
              <span className="ao-pill__icon">✕</span>
              {issue}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Issues for a suggestion: CSV list when available, else empty (breakdown will use API issuesString when present). */
function getIssuesForSuggestion(suggestionName: string): string[] {
  return SUGGESTION_TO_ISSUES[suggestionName] ?? [];
}

function initialAddToPlanRowForSuggestion(
  suggestionName: string,
  what: string,
  providerCode?: string,
) {
  const quantity =
    what === "Skincare"
      ? ""
      : getQuantityContext(what, undefined, providerCode).defaultQuantity;
  return {
    suggestionName,
    what,
    where: [] as string[],
    when: TIMELINE_OPTIONS[0],
    product: "",
    quantity,
    notes: "",
  };
}

export interface TreatmentRecommenderBySuggestionProps {
  client: Client;
  onBack: () => void;
  onUpdate?: () => void | Promise<void>;
  /** Add item directly to plan and show success. Returns the new item for immediate UI. */
  onAddToPlanDirect?: (
    prefill: TreatmentPlanPrefill,
    options?: TreatmentPlanAddDirectOptions,
  ) => Promise<DiscussedItem | void> | void;
  /** Region filter chips — used when sending post-visit blueprint (AI mirror highlights). */
  onRecommenderRegionsChange?: (regions: readonly string[]) => void;
}

export default function TreatmentRecommenderBySuggestion({
  client,
  onBack: _onBack,
  onUpdate,
  onAddToPlanDirect,
  onRecommenderRegionsChange,
}: TreatmentRecommenderBySuggestionProps) {
  const { provider } = useDashboard();
  /** Item we just added so we can open it for editing when user clicks "Add additional details". Cleared when modal closes. */
  const [lastAddedItem, setLastAddedItem] = useState<DiscussedItem | null>(
    null,
  );
  const [filterState, setFilterState] =
    useState<TreatmentRecommenderFilterState>(() => ({
      ...DEFAULT_RECOMMENDER_FILTER_STATE,
    }));

  useEffect(() => {
    onRecommenderRegionsChange?.(filterState.region);
  }, [filterState.region, onRecommenderRegionsChange]);

  const [clientFrontPhotoUrl, setClientFrontPhotoUrl] = useState<string | null>(
    null,
  );
  /** Area name -> URL for area cropped photos from Patients table (fallback). */
  const [areaPhotoUrls, setAreaPhotoUrls] = useState<Record<string, string>>(
    {},
  );
  /** Suggestion name -> URL from patient-records API (Area Cropped Photos). Takes precedence when set. */
  const [suggestionPhotoUrls, setSuggestionPhotoUrls] = useState<
    Record<string, string>
  >({});
  /** Cards from patient-records API (one per suggestion with short summary, AI summary, etc.). When set, we use these as the card list. */
  const [apiCards, setApiCards] = useState<PatientSuggestionCard[]>([]);
  const [failedPhotoUrls, setFailedPhotoUrls] = useState<Set<string>>(
    new Set(),
  );
  const [photoExplorerInterest, setPhotoExplorerInterest] = useState<
    string | null
  >(null);
  const [addToPlanForSuggestion, setAddToPlanForSuggestion] = useState<{
    suggestionName: string;
    what: string;
    where: string[];
    when: string;
    product?: string;
    quantity?: string;
    notes?: string;
  } | null>(null);

  const getUrlFromAttachment = (att: {
    thumbnails?: { full?: { url?: string }; large?: { url?: string } };
    url?: string;
  }) =>
    att?.thumbnails?.full?.url ??
    att?.thumbnails?.large?.url ??
    att?.url ??
    null;

  const detectedIssues = useMemo(() => getDetectedIssues(client), [client]);

  const staticSuggestionList = useMemo(() => {
    let list = [...ALL_TREATMENT_INTERESTS];
    if (filterState.region.length > 0) {
      list = filterSuggestionsByRegion(list, filterState.region);
    }
    if (filterState.sameDayAddOn) {
      list = list.filter((name) => {
        const treatments = getTreatmentsForInterest(name, provider?.code);
        return treatments.some((t) =>
          (SAME_DAY_TREATMENTS as readonly string[]).includes(t),
        );
      });
    }
    return list;
  }, [filterState.region, filterState.sameDayAddOn, provider?.code]);

  /** When we have API cards, filter and sort them (focus first, then by name). Otherwise use static list. */
  const displayCards = useMemo(():
    | { type: "api"; cards: PatientSuggestionCard[] }
    | { type: "static"; names: string[] } => {
    if (apiCards.length === 0) {
      return { type: "static", names: staticSuggestionList };
    }
    let list = apiCards;
    if (filterState.region.length > 0) {
      const allowedNames = new Set(
        filterSuggestionsByRegion(
          apiCards.map((c) => c.suggestionName),
          filterState.region,
        ),
      );
      list = list.filter((c) => allowedNames.has(c.suggestionName));
    }
    if (filterState.sameDayAddOn) {
      list = list.filter((c) => {
        const treatments = getTreatmentsForInterest(c.suggestionName, provider?.code);
        return treatments.some((t) =>
          (SAME_DAY_TREATMENTS as readonly string[]).includes(t),
        );
      });
    }
    const sorted = [...list].sort((a, b) => {
      if (a.isFocusArea !== b.isFocusArea) return a.isFocusArea ? -1 : 1;
      return a.suggestionName.localeCompare(b.suggestionName);
    });
    return { type: "api", cards: sorted };
  }, [
    apiCards,
    filterState.region,
    filterState.sameDayAddOn,
    staticSuggestionList,
    provider?.code,
  ]);

  type CardViewItem =
    | { source: "api"; card: PatientSuggestionCard }
    | { source: "static"; suggestionName: string };
  const viewItems: CardViewItem[] = useMemo(() => {
    if (displayCards.type === "api") {
      return displayCards.cards.map((card) => ({
        source: "api" as const,
        card,
      }));
    }
    return displayCards.names.map((suggestionName) => ({
      source: "static" as const,
      suggestionName,
    }));
  }, [displayCards]);

  const handleAddToPlanConfirm = async () => {
    if (!addToPlanForSuggestion || !onAddToPlanDirect) return;
    const region =
      addToPlanForSuggestion.where.length > 0
        ? addToPlanForSuggestion.where.join(", ")
        : (SUGGESTION_TO_AREA[addToPlanForSuggestion.suggestionName] ?? "");
    const prefill: TreatmentPlanPrefill = {
      interest: addToPlanForSuggestion.suggestionName,
      region,
      treatment:
        (addToPlanForSuggestion.what?.trim() ||
          getTreatmentsForInterest(addToPlanForSuggestion.suggestionName, provider?.code)[0]) ??
        "",
      timeline: addToPlanForSuggestion.when,
      treatmentProduct: addToPlanForSuggestion.product?.trim() || undefined,
      quantity: addToPlanForSuggestion.quantity?.trim() || undefined,
      notes: addToPlanForSuggestion.notes?.trim() || undefined,
    };
    try {
      const newItem = await onAddToPlanDirect(prefill);
      setAddToPlanForSuggestion(null);
      if (newItem) setLastAddedItem(newItem);
    } catch {
      /* parent shows error */
    }
  };

  /** Whether this suggestion is already in the treatment plan (so we show "Added"). */
  const isSuggestionInPlan = (suggestionName: string): boolean => {
    if (lastAddedItem && lastAddedItem.interest === suggestionName) return true;
    return (client.discussedItems ?? []).some(
      (i) => i.interest === suggestionName,
    );
  };

  useEffect(() => {
    if (!addToPlanForSuggestion || addToPlanForSuggestion.what === "Skincare")
      return;
    const qtyCtx = getQuantityContext(
      addToPlanForSuggestion.what,
      addToPlanForSuggestion.product?.trim() || undefined,
      provider?.code,
    );
    if (qtyCtx.quantityControl === "text") return;
    const { options, defaultQuantity } = qtyCtx;
    const q = (addToPlanForSuggestion.quantity ?? "").trim();
    if (q && options.includes(q)) return;
    const next = defaultQuantity;
    if (q !== next) {
      setAddToPlanForSuggestion((prev) =>
        prev ? { ...prev, quantity: next } : null,
      );
    }
  }, [
    addToPlanForSuggestion?.what,
    addToPlanForSuggestion?.product,
    addToPlanForSuggestion?.quantity,
  ]);

  const isInPlanOrInterests = (suggestionName: string): boolean => {
    const interested = client.interestedIssues;
    if (interested && typeof interested === "string") {
      const list = interested.split(",").map((s) => s.trim());
      if (list.some((s) => s.toLowerCase() === suggestionName.toLowerCase()))
        return true;
    }
    const items = client.discussedItems ?? [];
    if (
      items.some(
        (item) =>
          (item.interest?.trim().toLowerCase() ?? "") ===
          suggestionName.toLowerCase(),
      )
    )
      return true;
    return false;
  };

  useEffect(() => {
    const areaPhotoFieldList = [
      ...new Set(Object.values(AREA_CROPPED_PHOTO_FIELDS)),
    ];

    if (!client || client.tableSource !== "Patients") {
      setClientFrontPhotoUrl(getClientFrontPhotoDisplayUrl(client?.frontPhoto));
      setAreaPhotoUrls({});
      return;
    }

    setClientFrontPhotoUrl(getClientFrontPhotoDisplayUrl(client.frontPhoto));

    let mounted = true;
    const fieldsToFetch = ["Front Photo", ...areaPhotoFieldList];
    fetchTableRecords("Patients", {
      filterFormula: `RECORD_ID() = "${client.id}"`,
      fields: fieldsToFetch,
    })
      .then((records) => {
        if (!mounted || records.length === 0) return;
        const fields = records[0].fields;

        const front = fields["Front Photo"] ?? fields["Front photo"];
        if (front && Array.isArray(front) && front.length > 0) {
          const url = getClientFrontPhotoDisplayUrl(front, {
            allowExpiringAirtableCdn: true,
          });
          setClientFrontPhotoUrl(url);
        } else {
          setClientFrontPhotoUrl(
            getClientFrontPhotoDisplayUrl(client.frontPhoto),
          );
        }

        const areaUrls: Record<string, string> = {};
        for (const [areaName, fieldName] of Object.entries(
          AREA_CROPPED_PHOTO_FIELDS,
        )) {
          const val = fields[fieldName];
          if (val && Array.isArray(val) && val.length > 0) {
            const url = getUrlFromAttachment(val[0]);
            if (url) areaUrls[areaName] = url;
          }
        }
        setAreaPhotoUrls(areaUrls);
      })
      .catch(() => {
        setClientFrontPhotoUrl(
          getClientFrontPhotoDisplayUrl(client.frontPhoto),
        );
        setAreaPhotoUrls({});
      });
    return () => {
      mounted = false;
    };
  }, [client]);

  // Fetch patient-records (Patient-Issue/Suggestion Mapping) for cards + photo map (SUGGESTION_CARDS_INTEGRATION.md).
  useEffect(() => {
    const email = client?.email?.trim();
    if (!email) {
      setSuggestionPhotoUrls({});
      setApiCards([]);
      return;
    }
    let mounted = true;
    fetchPatientRecords(email)
      .then((records) => {
        if (!mounted) return;
        const map: Record<string, string> = {};
        for (const record of records) {
          const fields = record.fields || {};
          const suggestionName = fields[PATIENT_RECORDS_SUGGESTION_NAME_FIELD];
          const photo = fields[PATIENT_RECORDS_PHOTO_FIELD];
          const url = getAreaCroppedPhotoUrl(photo);
          if (suggestionName && url) {
            const name =
              typeof suggestionName === "string"
                ? suggestionName
                : String(suggestionName);
            map[name] = url;
          }
        }
        setSuggestionPhotoUrls(map);
        setApiCards(parsePatientRecordsToCards(records));
      })
      .catch(() => {
        setSuggestionPhotoUrls({});
        setApiCards([]);
      });
    return () => {
      mounted = false;
    };
  }, [client?.email]);

  /**
   * Photo for a suggestion uses the same logic for all cards:
   * 1) patient-records API (exact or case-insensitive match on "Name (from Suggestions)");
   * 2) else Patients table area crop (by SUGGESTION_TO_AREA, e.g. Balance Brows → Forehead);
   * 3) else client front photo.
   * Missing photos usually mean no record/photo in patient-records for that suggestion and no area crop on the Patient.
   */
  const getPhotoUrlForSuggestion = (
    suggestionName: string,
    area: string | undefined,
  ): string | null => {
    const fromApi =
      suggestionPhotoUrls[suggestionName] ??
      Object.entries(suggestionPhotoUrls).find(
        ([key]) =>
          key.trim().toLowerCase() === suggestionName.trim().toLowerCase(),
      )?.[1];
    if (fromApi) return fromApi;
    if (area && areaPhotoUrls[area]) return areaPhotoUrls[area];
    return clientFrontPhotoUrl;
  };

  const skincareCarouselForQuiz = useMemo(
    () => getSkincareCarouselItems(provider),
    [provider],
  );
  const quizRoutineSections = useMemo(
    () =>
      buildQuizSkincareRoutineSections(
        client.skincareQuiz?.recommendedProductNames,
        client.skincareQuiz?.result,
        (name) => skincareCarouselForQuiz.find((p) => p.name === name),
        provider?.code,
        provider?.name,
      ),
    [
      client.skincareQuiz?.recommendedProductNames,
      client.skincareQuiz?.result,
      skincareCarouselForQuiz,
      provider?.code,
      provider?.name,
    ],
  );

  return (
    <div className="treatment-recommender-by-suggestion">
      <div className="treatment-recommender-by-suggestion__body">
        {!isWellnestWellnessProviderCode(provider?.code) ? (
          <TreatmentRecommenderFilters
            state={filterState}
            onStateChange={(next) => setFilterState((s) => ({ ...s, ...next }))}
          />
        ) : null}

        {client.skincareQuiz && (
          <div className="treatment-recommender-skin-analysis">
            <h3 className="treatment-recommender-skin-analysis__title">
              Recommended for you
            </h3>
            {client.skincareQuiz.completedAt && (
              <p className="treatment-recommender-skin-analysis__completed">
                Quiz Completed{" "}
                {new Date(client.skincareQuiz.completedAt).toLocaleDateString(
                  "en-US",
                  {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  },
                )}
              </p>
            )}
            <div className="treatment-recommender-skin-analysis__summary">
              <span className="treatment-recommender-skin-analysis__type">
                {client.skincareQuiz.resultLabel ??
                  (client.skincareQuiz.result
                    ? client.skincareQuiz.result.charAt(0).toUpperCase() +
                      client.skincareQuiz.result.slice(1)
                    : "Completed")}
              </span>
              {client.skincareQuiz.result &&
                GEMSTONE_BY_SKIN_TYPE[client.skincareQuiz.result] && (
                  <span className="treatment-recommender-skin-analysis__gemstone">
                    {" "}
                    · {
                      GEMSTONE_BY_SKIN_TYPE[client.skincareQuiz.result].name
                    }{" "}
                    {GEMSTONE_BY_SKIN_TYPE[client.skincareQuiz.result].emoji}{" "}
                    {GEMSTONE_BY_SKIN_TYPE[client.skincareQuiz.result].tagline}
                  </span>
                )}
            </div>
            {quizRoutineSections.length > 0 && (
              <div className="treatment-recommender-skin-analysis__routine-wrap">
                {quizRoutineSections.map((section) => (
                  <div
                    key={section.id}
                    className="treatment-recommender-skin-analysis__products"
                  >
                    <span className="treatment-recommender-skin-analysis__products-label">
                      {section.title}
                    </span>
                    <div className="treatment-recommender-skin-analysis__chips treatment-recommender-skin-analysis__chips--column">
                      {section.items.map((p) => (
                        <button
                          key={`${section.id}-${p.name}`}
                          type="button"
                          className="treatment-recommender-skin-analysis__chip treatment-recommender-skin-analysis__chip--add treatment-recommender-skin-analysis__chip--row"
                          onClick={async () => {
                            if (!onAddToPlanDirect) return;
                            const prefill: TreatmentPlanPrefill = {
                              interest: "",
                              region: "",
                              treatment: "Skincare",
                              treatmentProduct:
                                p.name.split("|")[0]?.trim() ?? p.name,
                              timeline: TIMELINE_OPTIONS[0],
                              notes: p.blurb || undefined,
                            };
                            try {
                              await onAddToPlanDirect(prefill);
                              showToast("Added to treatment plan");
                            } catch {
                              showToast("Could not add to plan");
                            }
                          }}
                          title={
                            p.blurb
                              ? `Add to plan – ${p.blurb}`
                              : "Add to plan"
                          }
                        >
                          {p.imageUrl ? (
                            <img
                              src={p.imageUrl}
                              alt=""
                              className="treatment-recommender-skin-analysis__chip-thumb"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                            />
                          ) : (
                            <span className="treatment-recommender-skin-analysis__chip-placeholder">
                              ◆
                            </span>
                          )}
                          <span className="treatment-recommender-skin-analysis__chip-name">
                            {p.displayName}
                          </span>
                          {p.blurb ? (
                            <span className="treatment-recommender-skin-analysis__chip-context">
                              {p.blurb}
                            </span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <h2 className="treatment-recommender-by-suggestion__results-heading">
          {viewItems.length} suggestion{viewItems.length !== 1 ? "s" : ""}
        </h2>

        <div className="treatment-recommender-by-suggestion__cards">
          {viewItems.length === 0 ? (
            <p className="treatment-recommender-by-suggestion__empty">
              No suggestions match the current filters. Try expanding filters or
              changing region, findings, or same-day.
            </p>
          ) : (
            viewItems.map((item) => {
              const suggestionName =
                item.source === "api"
                  ? item.card.suggestionName
                  : item.suggestionName;
              const areaNamesRaw =
                item.source === "api" ? item.card.areaNames : undefined;
              const areaNamesArr =
                areaNamesRaw == null
                  ? []
                  : Array.isArray(areaNamesRaw)
                    ? areaNamesRaw
                    : String(areaNamesRaw)
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
              const area =
                item.source === "api"
                  ? (areaNamesArr[0] ?? SUGGESTION_TO_AREA[suggestionName])
                  : SUGGESTION_TO_AREA[suggestionName];
              const cardPhotoUrl =
                item.source === "api" && item.card.photoUrl
                  ? item.card.photoUrl
                  : getPhotoUrlForSuggestion(suggestionName, area ?? undefined);
              const fullSuggestionIssues =
                getIssuesForSuggestion(suggestionName);
              const issuesList =
                fullSuggestionIssues.length > 0
                  ? fullSuggestionIssues
                  : item.source === "api" && item.card.issuesString
                    ? item.card.issuesString
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)
                    : [];
              const breakdownRows = groupIssuesByConcern(issuesList);
              const inPlan = isInPlanOrInterests(suggestionName);
              const showPhoto =
                cardPhotoUrl && !failedPhotoUrls.has(cardPhotoUrl);
              const detectedForCard = (() => {
                const set = new Set(detectedIssues);
                if (item.source === "api" && item.card.issuesString) {
                  const fromApi = item.card.issuesString
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
                  fromApi.forEach((issue) => set.add(normalizeIssue(issue)));
                }
                return set;
              })();
              return (
                <div
                  key={suggestionName}
                  className="treatment-recommender-by-suggestion__card"
                >
                  <div className="treatment-recommender-by-suggestion__card-top">
                    {showPhoto && cardPhotoUrl ? (
                      <div className="treatment-recommender-by-suggestion__photo-wrap">
                        <img
                          src={cardPhotoUrl}
                          alt={client.name}
                          className="treatment-recommender-by-suggestion__photo"
                          onError={() =>
                            setFailedPhotoUrls((prev) =>
                              new Set(prev).add(cardPhotoUrl),
                            )
                          }
                        />
                      </div>
                    ) : null}
                    <div className="treatment-recommender-by-suggestion__card-main">
                      <h2 className="treatment-recommender-by-suggestion__card-title">
                        {suggestionName}
                      </h2>
                      {inPlan && (
                        <p className="treatment-recommender-by-suggestion__in-plan">
                          Added to treatment interests
                        </p>
                      )}
                      {item.source === "api" &&
                        (item.card.shortSummary ?? item.card.aiSummary) && (
                          <div className="treatment-recommender-by-suggestion__summary">
                            {item.card.shortSummary && (
                              <p className="treatment-recommender-by-suggestion__short-summary">
                                {item.card.shortSummary}
                              </p>
                            )}
                            {item.card.aiSummary && (
                              <details className="treatment-recommender-by-suggestion__ai-summary">
                                <summary>Learn more</summary>
                                <p className="treatment-recommender-by-suggestion__ai-summary-text">
                                  {item.card.aiSummary}
                                </p>
                              </details>
                            )}
                          </div>
                        )}
                      <div className="treatment-recommender-by-suggestion__breakdown">
                        <h3 className="treatment-recommender-by-suggestion__breakdown-title">
                          Feature breakdown
                        </h3>
                        {breakdownRows.length > 0 ? (
                          breakdownRows.map((row) => (
                            <FeatureBreakdownRow
                              key={row.label}
                              label={row.label}
                              issues={row.issues}
                              detectedIssues={detectedForCard}
                            />
                          ))
                        ) : (
                          <p className="treatment-recommender-by-suggestion__no-breakdown">
                            No findings mapped for this suggestion.
                          </p>
                        )}
                      </div>
                      <div className="treatment-recommender-by-suggestion__card-actions">
                        <div className="treatment-recommender-by-suggestion__add-section">
                          {isSuggestionInPlan(suggestionName) ? (
                            <div className="treatment-recommender-by-suggestion__added-state">
                              <p className="treatment-recommender-by-suggestion__added-message">
                                Added to treatment plan
                              </p>
                            </div>
                          ) : addToPlanForSuggestion?.suggestionName ===
                            suggestionName ? (
                            <div className="treatment-recommender-by-suggestion__add-form">
                              <div className="treatment-recommender-by-suggestion__add-row plan-add-row">
                                <span className="plan-add-row-label">Type:</span>
                                <div className="treatment-recommender-by-suggestion__chips plan-add-chips">
                                  {getTreatmentsForInterest(
                                    addToPlanForSuggestion.suggestionName,
                                    provider?.code,
                                  ).map((t) => (
                                    <button
                                      key={t}
                                      type="button"
                                      className={`treatment-recommender-by-suggestion__chip plan-add-chip${
                                        addToPlanForSuggestion.what === t
                                          ? " treatment-recommender-by-suggestion__chip--selected plan-add-chip--selected"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setAddToPlanForSuggestion((prev) =>
                                          prev ? { ...prev, what: t } : null,
                                        )
                                      }
                                    >
                                      {t}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="treatment-recommender-by-suggestion__add-row plan-add-row">
                                <span className="plan-add-row-label">Where:</span>
                                <div className="treatment-recommender-by-suggestion__chips plan-add-chips">
                                  {REGION_OPTIONS.filter(
                                    (r) => r !== "Multiple" && r !== "Other",
                                  ).map((r) => (
                                    <button
                                      key={r}
                                      type="button"
                                      className={`treatment-recommender-by-suggestion__chip plan-add-chip${
                                        addToPlanForSuggestion.where.includes(r)
                                          ? " treatment-recommender-by-suggestion__chip--selected plan-add-chip--selected"
                                          : ""
                                      }`}
                                      onClick={() => {
                                        setAddToPlanForSuggestion((prev) =>
                                          prev
                                            ? {
                                                ...prev,
                                                where: prev.where.includes(r)
                                                  ? prev.where.filter(
                                                      (x) => x !== r,
                                                    )
                                                  : [...prev.where, r],
                                              }
                                            : null,
                                        );
                                      }}
                                    >
                                      {r}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="treatment-recommender-by-suggestion__add-row plan-add-row">
                                <span className="plan-add-row-label">When:</span>
                                <div className="treatment-recommender-by-suggestion__chips plan-add-chips">
                                  {TIMELINE_OPTIONS.filter(
                                    (t) => t !== "Completed",
                                  ).map((t) => (
                                    <button
                                      key={t}
                                      type="button"
                                      className={`treatment-recommender-by-suggestion__chip plan-add-chip${
                                        addToPlanForSuggestion.when === t
                                          ? " treatment-recommender-by-suggestion__chip--selected plan-add-chip--selected"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setAddToPlanForSuggestion((prev) =>
                                          prev ? { ...prev, when: t } : null,
                                        )
                                      }
                                    >
                                      {timelineOptionDisplayLabel(t)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              {addToPlanForSuggestion.what !== "Skincare" &&
                                shouldShowProminentPlanQuantity(
                                  addToPlanForSuggestion.what,
                                  addToPlanForSuggestion.product?.trim() ||
                                    undefined,
                                ) &&
                                (() => {
                                  const qtyCtx = getQuantityContext(
                                    addToPlanForSuggestion.what,
                                    addToPlanForSuggestion.product?.trim() ||
                                      undefined,
                                    provider?.code,
                                  );
                                  return (
                                    <label className="treatment-recommender-by-suggestion__details-label treatment-recommender-by-suggestion__pricing-qty">
                                      <span className="treatment-recommender-by-suggestion__pricing-qty-label">
                                        {qtyCtx.unitLabel}
                                      </span>
                                      {qtyCtx.quantityControl === "text" ? (
                                        <input
                                          type="text"
                                          inputMode="numeric"
                                          className="treatment-recommender-by-suggestion__details-input"
                                          aria-label={qtyCtx.unitLabel}
                                          placeholder={qtyCtx.defaultQuantity}
                                          value={
                                            addToPlanForSuggestion.quantity ??
                                            ""
                                          }
                                          onChange={(e) => {
                                            const v = e.target.value.replace(
                                              /\D/g,
                                              "",
                                            );
                                            setAddToPlanForSuggestion(
                                              (prev) =>
                                                prev
                                                  ? { ...prev, quantity: v }
                                                  : null,
                                            );
                                          }}
                                        />
                                      ) : (
                                        <PlanQuantityStepperInput
                                          unitLabel={qtyCtx.unitLabel}
                                          quantity={
                                            addToPlanForSuggestion.quantity ??
                                            ""
                                          }
                                          options={qtyCtx.options}
                                          defaultQuantity={
                                            qtyCtx.defaultQuantity
                                          }
                                          inputId={`suggestion-qty-prominent-${addToPlanForSuggestion.what}`}
                                          onQuantityChange={(next) =>
                                            setAddToPlanForSuggestion((prev) =>
                                              prev
                                                ? { ...prev, quantity: next }
                                                : null,
                                            )
                                          }
                                        />
                                      )}
                                    </label>
                                  );
                                })()}
                              <details className="treatment-recommender-by-suggestion__details plan-opt-details">
                                <summary>Optional details</summary>
                                <div className="treatment-recommender-by-suggestion__details-fields plan-opt-fields plan-opt-fields-inner">
                                  {addToPlanForSuggestion.what !== "Skincare" &&
                                  !shouldShowProminentPlanQuantity(
                                    addToPlanForSuggestion.what,
                                    addToPlanForSuggestion.product?.trim() ||
                                      undefined,
                                  )
                                    ? (() => {
                                        const qtyCtx = getQuantityContext(
                                          addToPlanForSuggestion.what,
                                          addToPlanForSuggestion.product?.trim() ||
                                            undefined,
                                          provider?.code,
                                        );
                                        const oid = planOptDomIdSuffix(
                                          addToPlanForSuggestion.what,
                                        );
                                        return (
                                          <section
                                            className="plan-opt-section"
                                            aria-labelledby={`suggestion-plan-opt-qty-${oid}`}
                                          >
                                            <h4
                                              className="plan-opt-section__title"
                                              id={`suggestion-plan-opt-qty-${oid}`}
                                            >
                                              Quantity
                                            </h4>
                                            <div className="plan-opt-section__body">
                                              <label className="treatment-recommender-by-suggestion__details-label">
                                                <span className="treatment-recommender-by-suggestion__quantity-unit-label">
                                                  {qtyCtx.unitLabel}
                                                </span>
                                                {qtyCtx.quantityControl === "text" ? (
                                                  <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    className="treatment-recommender-by-suggestion__details-input"
                                                    aria-label={qtyCtx.unitLabel}
                                                    placeholder={
                                                      qtyCtx.defaultQuantity
                                                    }
                                                    value={
                                                      addToPlanForSuggestion.quantity ??
                                                      ""
                                                    }
                                                    onChange={(e) => {
                                                      const v =
                                                        e.target.value.replace(
                                                          /\D/g,
                                                          "",
                                                        );
                                                      setAddToPlanForSuggestion(
                                                        (prev) =>
                                                          prev
                                                            ? {
                                                                ...prev,
                                                                quantity: v,
                                                              }
                                                            : null,
                                                      );
                                                    }}
                                                  />
                                                ) : (
                                                  <PlanQuantityStepperInput
                                                    unitLabel={qtyCtx.unitLabel}
                                                    quantity={
                                                      addToPlanForSuggestion.quantity ??
                                                      ""
                                                    }
                                                    options={qtyCtx.options}
                                                    defaultQuantity={
                                                      qtyCtx.defaultQuantity
                                                    }
                                                    inputId={`suggestion-qty-details-${addToPlanForSuggestion.what}`}
                                                    onQuantityChange={(next) =>
                                                      setAddToPlanForSuggestion(
                                                        (prev) =>
                                                          prev
                                                            ? {
                                                                ...prev,
                                                                quantity: next,
                                                              }
                                                            : null,
                                                      )
                                                    }
                                                  />
                                                )}
                                              </label>
                                            </div>
                                          </section>
                                        );
                                      })()
                                    : null}
                                  <section
                                    className="plan-opt-section"
                                    aria-labelledby={`suggestion-plan-opt-product-${planOptDomIdSuffix(addToPlanForSuggestion.what)}`}
                                  >
                                    <h4
                                      className="plan-opt-section__title"
                                      id={`suggestion-plan-opt-product-${planOptDomIdSuffix(addToPlanForSuggestion.what)}`}
                                    >
                                      Product
                                    </h4>
                                    <div className="plan-opt-section__body">
                                      <input
                                        type="text"
                                        className="treatment-recommender-by-suggestion__details-input plan-opt-input"
                                        placeholder="e.g. Juvederm, Botox"
                                        value={
                                          addToPlanForSuggestion.product ?? ""
                                        }
                                        onChange={(e) =>
                                          setAddToPlanForSuggestion((prev) =>
                                            prev
                                              ? {
                                                  ...prev,
                                                  product: e.target.value,
                                                }
                                              : null,
                                          )
                                        }
                                        aria-labelledby={`suggestion-plan-opt-product-${planOptDomIdSuffix(addToPlanForSuggestion.what)}`}
                                      />
                                    </div>
                                  </section>
                                  <section
                                    className="plan-opt-section plan-opt-section--notes"
                                    aria-labelledby={`suggestion-plan-opt-notes-${planOptDomIdSuffix(addToPlanForSuggestion.what)}`}
                                  >
                                    <h4
                                      className="plan-opt-section__title"
                                      id={`suggestion-plan-opt-notes-${planOptDomIdSuffix(addToPlanForSuggestion.what)}`}
                                    >
                                      Notes
                                    </h4>
                                    <textarea
                                      className="treatment-recommender-by-suggestion__details-textarea plan-opt-textarea"
                                      placeholder="Optional notes for this line item"
                                      rows={2}
                                      value={addToPlanForSuggestion.notes ?? ""}
                                      onChange={(e) =>
                                        setAddToPlanForSuggestion((prev) =>
                                          prev
                                            ? { ...prev, notes: e.target.value }
                                            : null,
                                        )
                                      }
                                      aria-labelledby={`suggestion-plan-opt-notes-${planOptDomIdSuffix(addToPlanForSuggestion.what)}`}
                                    />
                                  </section>
                                </div>
                              </details>
                              <div className="treatment-recommender-by-suggestion__add-actions plan-add-actions">
                                <button
                                  type="button"
                                  className="treatment-recommender-by-suggestion__add-btn treatment-recommender-by-suggestion__confirm-btn plan-add-confirm-btn"
                                  onClick={handleAddToPlanConfirm}
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  className="treatment-recommender-by-suggestion__cancel-btn plan-add-cancel-btn"
                                  onClick={() =>
                                    setAddToPlanForSuggestion(null)
                                  }
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : onAddToPlanDirect ? (
                            <button
                              type="button"
                              className="treatment-recommender-by-suggestion__add-btn"
                              onClick={() => {
                                const treatments =
                                  getTreatmentsForInterest(
                                    suggestionName,
                                    provider?.code,
                                  );
                                const what = treatments[0] ?? "";
                                setAddToPlanForSuggestion(
                                  initialAddToPlanRowForSuggestion(
                                    suggestionName,
                                    what,
                                    provider?.code,
                                  ),
                                );
                              }}
                            >
                              Add to plan
                            </button>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="treatment-recommender-by-suggestion__examples-btn"
                          onClick={() =>
                            setPhotoExplorerInterest(suggestionName)
                          }
                        >
                          View examples
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {photoExplorerInterest && (
        <TreatmentPhotosModal
          client={client}
          interest={photoExplorerInterest}
          selectedRegion={SUGGESTION_TO_AREA[photoExplorerInterest]}
          onClose={() => setPhotoExplorerInterest(null)}
          onUpdate={onUpdate}
          onAddToPlanDirect={
            onAddToPlanDirect
              ? async (prefill, options) => {
                  setPhotoExplorerInterest(null);
                  await onAddToPlanDirect(prefill, options);
                }
              : undefined
          }
          planItems={client.discussedItems ?? []}
        />
      )}
    </div>
  );
}
